import React from 'react';
import ButtonBasic from '../../atoms/buttonBasic/view';
import { LogIn, AlertTriangle, FileWarning, PackageMinus, Hash, FileText } from 'lucide-react';
import { Avatar, Typography, Box, Tooltip } from '@mui/material';
import './style.css';

/**
 * LinkedCompanyCard Molecule
 * Displays an active company where the accountant has access.
 */
function LinkedCompanyCard({
    companyName,
    ownerName,
    ownerEmail,
    avatarUrl,
    onEnter,
    onAlertClick,
    onOpenDrafts,
    pendingDraftsCount = 0,
    isLoading,
    alertStatus = 'green',
    alerts = null
}) {
    const initials = companyName?.charAt(0).toUpperCase() || '?';

    const renderAlerts = () => {
        if (!alerts) return null;

        const items = [
            { key: 'rejected_invoices', icon: <FileWarning size={12} />, label: 'Conflictos de facturacion', isWarning: false },
            { key: 'resolutions_warning', icon: <AlertTriangle size={12} />, label: 'Alertas de resolucion', isWarning: true },
            { key: 'sequence_gaps', icon: <Hash size={12} />, label: 'Huecos en secuencia de facturas electronicas', isWarning: true },
            { key: 'negative_inventory', icon: <PackageMinus size={12} />, label: 'Stock negativo', isWarning: true },
        ];

        return (
            <div className="alerts-summary">
                {items.map(item => alerts[item.key] > 0 && (
                    <Tooltip key={item.key} title={`${alerts[item.key]} ${item.label} (Clic para ir)`}>
                        <div
                            className={`alert-msg-count actionable ${item.isWarning ? 'warning' : ''}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                onAlertClick && onAlertClick(item.key);
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            {item.icon}
                            <span>{alerts[item.key]}</span>
                        </div>
                    </Tooltip>
                ))}
            </div>
        );
    };

    return (
        <div className="accountant-card linked-company-card">
            {/* Fiscal Health Badge */}
            <Tooltip title={`Estado fiscal: ${alertStatus === 'green' ? 'Saludable' : (alertStatus === 'yellow' ? 'Atención' : 'Crítico')}`}>
                <div className={`fiscal-health-badge status-${alertStatus}`} />
            </Tooltip>

            {/* Pending Drafts Count Badge */}
            {pendingDraftsCount > 0 && (
                <Tooltip title={`${pendingDraftsCount} documentos pendientes en Auxiliar Contable`}>
                    <div className="drafts-count-badge">
                        {pendingDraftsCount} pendientes
                    </div>
                </Tooltip>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Avatar
                    src={avatarUrl}
                    sx={{
                        width: 52,
                        height: 52,
                        bgcolor: 'var(--color-primary-light, #e0f2f1)',
                        color: 'var(--color-primary, #00897b)',
                        fontWeight: 700,
                        fontSize: '1.2rem',
                        border: '2px solid var(--color-primary-10)'
                    }}
                >
                    {initials}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Tooltip title={companyName}>
                        <Typography
                            variant="h6"
                            className="accountant-card-title"
                            sx={{
                                fontSize: '1.1rem',
                                fontWeight: 700,
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {companyName}
                        </Typography>
                    </Tooltip>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: '0.85rem', fontWeight: 500 }}
                    >
                        {ownerName}
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ flex: 1 }}>
                <Typography
                    variant="caption"
                    className="accountant-card-email"
                    sx={{
                        display: 'block',
                        mb: 1.5,
                        opacity: 0.8,
                        wordBreak: 'break-all'
                    }}
                >
                    {ownerEmail}
                </Typography>

                {renderAlerts()}
            </Box>

            <Box sx={{ mt: 'auto' }}>
                <div className="accountant-card-actions">
                    <ButtonBasic
                        id="open-drafts"
                        title="Auxiliar Contable"
                        type="pill-secondary"
                        onClick={onOpenDrafts}
                        disabled={isLoading}
                        icon={<FileText size={18} style={{ marginRight: '8px' }} />}
                    />
                    <ButtonBasic
                        id="enter-company"
                        title="Entrar"
                        type="pill-primary"
                        onClick={onEnter}
                        disabled={isLoading}
                        icon={<LogIn size={18} style={{ marginRight: '8px' }} />}
                    />
                </div>
            </Box>
        </div>
    );
}

export default LinkedCompanyCard;
